import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type { ISnowflakeAccountsState, ISnowflakeAccount } from '../interfaces/ISnowflakeAccountManagement';
import * as snowflakeAccountsApi from '../services/snowflakeAccountsApi';

const initialState: ISnowflakeAccountsState = {
  accounts: [],
  loading: false,
  error: null,
  dialogError: null,
  managedKeyData: null,
  validationStatus: 'idle',
  validationMessage: null,
};

// Async thunks

/**
 * Fetch all Snowflake accounts for the authenticated user
 */
export const fetchSnowflakeAccounts = createAsyncThunk(
  'snowflakeAccounts/fetchAll',
  async (token: string): Promise<ISnowflakeAccount[]> => {
    return await snowflakeAccountsApi.fetchAccounts(token);
  },
);

/**
 * Create a new Snowflake account with managed key authentication
 */
export const createManagedKeyAccount = createAsyncThunk(
  'snowflakeAccounts/createManagedKey',
  async ({
    token,
    accountIdentifier,
    username,
    warehouse,
    role,
  }: {
    token: string;
    accountIdentifier: string;
    username: string;
    warehouse: string;
    role: string;
  }) => {
    return await snowflakeAccountsApi.createManagedKeyAccount(token, accountIdentifier, username, warehouse, role);
  },
);

/**
 * Delete a Snowflake account
 */
export const deleteSnowflakeAccount = createAsyncThunk(
  'snowflakeAccounts/delete',
  async ({ token, accountId }: { token: string; accountId: string }) => {
    await snowflakeAccountsApi.deleteAccount(token, accountId);
    return accountId;
  },
);

/**
 * Confirm and test Snowflake account connection
 */
export const confirmSnowflakeConnection = createAsyncThunk(
  'snowflakeAccounts/confirm',
  async ({ token, accountId }: { token: string; accountId: string }): Promise<void> => {
    await snowflakeAccountsApi.confirmConnection(token, accountId);
  },
);

/**
 * Fetch managed key account details (including public key)
 */
export const fetchManagedKeyAccountDetails = createAsyncThunk(
  'snowflakeAccounts/fetchManagedKeyDetails',
  async ({ token, accountId }: { token: string; accountId: string }) => {
    const accountDetails = await snowflakeAccountsApi.getAccountDetails(token, accountId);
    return accountDetails;
  },
);

/**
 * Update a managed key account
 */
export const updateManagedKeyAccount = createAsyncThunk(
  'snowflakeAccounts/updateManagedKey',
  async ({
    token,
    accountId,
    username,
    warehouse,
    role,
  }: {
    token: string;
    accountId: string;
    username: string;
    warehouse: string;
    role: string;
  }) => {
    await snowflakeAccountsApi.updateManagedKeyAccount(token, accountId, username, warehouse, role);
    return { accountId, username, warehouse, role };
  },
);

/**
 * Validate a Snowflake account identifier
 */
export const validateSnowflakeAccountIdentifier = createAsyncThunk(
  'snowflakeAccounts/validate',
  async ({ token, identifier }: { token: string; identifier: string }) => {
    return await snowflakeAccountsApi.validateAccountIdentifier(token, identifier);
  },
);

// Slice

const snowflakeAccountsSlice = createSlice({
  name: 'snowflakeAccounts',
  initialState,
  reducers: {
    /**
     * Clear managed key data after setup is complete
     */
    clearManagedKeyData: (state) => {
      state.managedKeyData = null;
    },
    /**
     * Clear error messages
     */
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Clear dialog error messages
     */
    clearDialogError: (state) => {
      state.dialogError = null;
    },
    /**
     * Reset validation state
     */
    resetValidation: (state) => {
      state.validationStatus = 'idle';
      state.validationMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch accounts
    builder.addCase(fetchSnowflakeAccounts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSnowflakeAccounts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message ?? 'Failed to fetch accounts';
    });
    builder.addCase(fetchSnowflakeAccounts.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.accounts = action.payload;
    });

    // Create managed key account
    builder.addCase(createManagedKeyAccount.pending, (state) => {
      state.loading = true;
      state.dialogError = null;
    });
    builder.addCase(createManagedKeyAccount.rejected, (state, action) => {
      state.loading = false;
      state.dialogError = action.error.message ?? 'Failed to create account';
    });
    builder.addCase(createManagedKeyAccount.fulfilled, (state, action) => {
      state.loading = false;
      state.dialogError = null;
      state.managedKeyData = action.payload.managedKeyData;
      // Add the new account to the accounts list (in pending state)
      const existingIndex = state.accounts.findIndex((acc) => acc.id === action.payload.account.id);
      if (existingIndex !== -1) {
        state.accounts[existingIndex] = action.payload.account;
      } else {
        state.accounts.push(action.payload.account);
      }
    });

    // Delete account
    builder.addCase(deleteSnowflakeAccount.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteSnowflakeAccount.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message ?? 'Failed to delete account';
    });
    builder.addCase(deleteSnowflakeAccount.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      // Remove the deleted account from the list
      state.accounts = state.accounts.filter((acc) => acc.id.toString() !== action.payload);
    });

    // Confirm connection
    builder.addCase(confirmSnowflakeConnection.pending, (state) => {
      state.loading = true;
      state.dialogError = null;
    });
    builder.addCase(confirmSnowflakeConnection.rejected, (state, action) => {
      state.loading = false;
      state.dialogError = action.error.message ?? 'Failed to confirm connection';
    });
    builder.addCase(confirmSnowflakeConnection.fulfilled, (state, action) => {
      state.loading = false;
      state.dialogError = null;
      // Clear managed key data after successful confirmation
      state.managedKeyData = null;
    });

    // Fetch managed key account details
    builder.addCase(fetchManagedKeyAccountDetails.pending, (state) => {
      state.loading = true;
      state.dialogError = null;
    });
    builder.addCase(fetchManagedKeyAccountDetails.rejected, (state, action) => {
      state.loading = false;
      state.dialogError = action.error.message ?? 'Failed to fetch account details';
    });
    builder.addCase(fetchManagedKeyAccountDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.dialogError = null;
      state.managedKeyData = action.payload.managedKeyData;
    });

    // Update managed key account
    builder.addCase(updateManagedKeyAccount.pending, (state) => {
      state.loading = true;
      state.dialogError = null;
    });
    builder.addCase(updateManagedKeyAccount.rejected, (state, action) => {
      state.loading = false;
      state.dialogError = action.error.message ?? 'Failed to update account';
    });
    builder.addCase(updateManagedKeyAccount.fulfilled, (state, action) => {
      state.loading = false;
      state.dialogError = null;
      // Update the account in the list
      const accountIndex = state.accounts.findIndex((acc) => acc.id.toString() === action.payload.accountId);
      if (accountIndex !== -1) {
        state.accounts[accountIndex] = {
          ...state.accounts[accountIndex],
          username: action.payload.username,
          warehouse: action.payload.warehouse,
          role: action.payload.role,
        };
      }
    });

    // Validate account identifier
    builder.addCase(validateSnowflakeAccountIdentifier.pending, (state) => {
      state.validationStatus = 'validating';
      state.validationMessage = null;
    });
    builder.addCase(validateSnowflakeAccountIdentifier.rejected, (state, action) => {
      state.validationStatus = 'invalid';
      state.validationMessage = action.error.message ?? 'Validation failed';
    });
    builder.addCase(validateSnowflakeAccountIdentifier.fulfilled, (state, action) => {
      state.validationStatus = action.payload.isValid ? 'valid' : 'invalid';
      state.validationMessage = action.payload.message;
    });
  },
});

// Actions
export const { clearManagedKeyData, clearError, clearDialogError, resetValidation } = snowflakeAccountsSlice.actions;

// Selectors
export const selectAccounts = (state: RootState) => {
  // Ensure accounts is always an array (handles redux-persist rehydration issues)
  const accounts = state.snowflakeAccounts.accounts;
  return Array.isArray(accounts) ? accounts : [];
};
export const selectLoading = (state: RootState) => state.snowflakeAccounts.loading;
export const selectError = (state: RootState) => state.snowflakeAccounts.error;
export const selectDialogError = (state: RootState) => state.snowflakeAccounts.dialogError;
export const selectManagedKeyData = (state: RootState) => state.snowflakeAccounts.managedKeyData;
export const selectValidationStatus = (state: RootState) => state.snowflakeAccounts.validationStatus;
export const selectValidationMessage = (state: RootState) => state.snowflakeAccounts.validationMessage;

export default snowflakeAccountsSlice.reducer;
